/**
 * Regras de exibição do profissional, compartilhadas pela LISTAGEM e pela FICHA.
 *
 * Estavam locais dentro de `ProfessionalsListPage`. A ficha
 * (`ProfessionalDetailsPage`) precisa exatamente das mesmas — e a precedência
 * de conselho é CONTRATO com o `ProfessionalRegistrationResolver` do backend,
 * não estética: é ela que decide qual registro aparece na tela e qual sai
 * impresso no documento. Duas cópias divergiriam em silêncio, e a tela passaria
 * a discordar do laudo.
 */
import { Professional, ProfessionalMeta, ProfessionalType } from '../../types/professional';

/** Cor do chip de situação (Ativo, Inativo, Suspenso, Bloqueado, Pendente). */
export const STATUS_COLORS: Record<string, 'success' | 'default' | 'warning' | 'error' | 'info'> = {
  'Ativo': 'success',
  'Inativo': 'default',
  'Suspenso': 'warning',
  'Bloqueado': 'error',
  'Pendente': 'info',
};

/** CPF em 000.000.000-00. Valor com tamanho inesperado sai como veio. */
export const formatCPF = (cpf: string): string => {
  const clean = cpf.replace(/\D/g, '');
  if (clean.length !== 11) return cpf;
  return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/** Campos booleanos do `meta` chegam como 'true'/'false' do banco. */
export const isMetaTruthy = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return Boolean(value);
};

export const isProfessionalActive = (professional: Professional): boolean =>
  professional.situacao === 'Ativo';

/**
 * Sem tipo gravado, o CONSELHO não é usado para adivinhar o tipo (#345).
 *
 * Um conselho serve a vários tipos: CREA é de engenheiro de segurança, de
 * eletricista e de mecânico; CRM é de médico do trabalho e de examinador. O
 * chute antigo ("tem CREA, logo é engenheiro de segurança") rotulava errado o
 * cadastro antigo, e rótulo errado é pior que rótulo nenhum — o registro
 * verdadeiro aparece ao lado.
 *
 * O MTB fica: não é conselho, é registro ministerial, e no sistema inteiro
 * pertence a um tipo só.
 */
export const getProfessionalType = (professional: Professional): string => {
  if (professional.meta?.professionalType) {
    return professional.meta.professionalType;
  }
  const { meta } = professional;
  if (!meta) return '';
  if (meta.mtb) return ProfessionalType.TECNICO_SEGURANCA;
  return '';
};

/**
 * Chaves de registro, na ORDEM DE FALLBACK do
 * `ProfessionalRegistrationResolver` do backend (`ORDEM_FALLBACK`).
 *
 * A ordem é contrato, não estética: é ela que decide qual conselho a tela
 * mostra quando o profissional tem mais de um, e o backend usa a mesma para
 * decidir qual sai impresso no documento. Divergir aqui faz a tela discordar
 * do laudo.
 *
 * O rótulo acompanha o do backend (`ROTULO_POR_CHAVE`), com uma exceção
 * proposital: MTB, que lá sai como "MTE" no documento e aqui continua "MTB",
 * o nome do campo no cadastro.
 */
export const REGISTRO_POR_CHAVE: ReadonlyArray<{ chave: keyof ProfessionalMeta; rotulo: string }> = [
  { chave: 'crm', rotulo: 'CRM' },
  { chave: 'crea', rotulo: 'CREA' },
  { chave: 'crfa', rotulo: 'CRFa' },
  { chave: 'crefito', rotulo: 'CREFITO' },
  { chave: 'crq', rotulo: 'CRQ' },
  { chave: 'crp', rotulo: 'CRP' },
  { chave: 'coren', rotulo: 'COREN' },
  { chave: 'cro', rotulo: 'CRO' },
  { chave: 'cra', rotulo: 'CRA' },
  { chave: 'mtb', rotulo: 'MTB' },
];

/** Conselho de cada tipo — espelha o `CHAVE_POR_TIPO` do resolver do backend. */
export const CHAVE_POR_TIPO: Partial<Record<ProfessionalType, keyof ProfessionalMeta>> = {
  [ProfessionalType.MEDICO_TRABALHO]: 'crm',
  [ProfessionalType.MEDICO_EXAMINADOR]: 'crm',
  [ProfessionalType.ENGENHEIRO_SEGURANCA]: 'crea',
  [ProfessionalType.ENGENHEIRO_ELETRICISTA]: 'crea',
  [ProfessionalType.ENGENHEIRO_MECANICO]: 'crea',
  [ProfessionalType.TECNICO_SEGURANCA]: 'mtb',
  [ProfessionalType.FONOAUDIOLOGO]: 'crfa',
  [ProfessionalType.PSICOLOGO]: 'crp',
  [ProfessionalType.ENFERMEIRO]: 'coren',
  [ProfessionalType.TECNICO_ENFERMAGEM]: 'coren',
  [ProfessionalType.ERGONOMISTA]: 'crefito',
};

/**
 * O conselho a exibir, pela mesma regra do backend: o do TIPO do profissional
 * quando está preenchido, senão o primeiro da ordem de fallback.
 *
 * O tipo só vale se a chave dele tiver valor — sem isso, o ergonomista que é
 * engenheiro (tipo aponta CREFITO, registro é CREA) sairia sem conselho.
 */
export const getRegistration = (professional: Professional): string => {
  const { meta } = professional;
  if (!meta) return '-';

  const doTipo = meta.professionalType
    ? CHAVE_POR_TIPO[meta.professionalType as ProfessionalType]
    : undefined;

  const escolhida =
    doTipo && meta[doTipo]
      ? REGISTRO_POR_CHAVE.find((registro) => registro.chave === doTipo)
      : REGISTRO_POR_CHAVE.find((registro) => Boolean(meta[registro.chave]));

  if (escolhida) return `${escolhida.rotulo}: ${String(meta[escolhida.chave])}`;
  if (meta.registrationNumber) return meta.registrationNumber;
  return '-';
};

/** Iniciais para o avatar da ficha: até duas letras do nome. */
export const getInitials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0])
    .join('')
    .toUpperCase();
